import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ConfirmationDialog from '../components/ConfirmationDialog';
import CircuitEditor, { countryCodes } from '../components/CircuitEditor';
import CircuitDatabase from '../components/CircuitDatabase';
import { updateLastModified } from '../utils/backup';
import 'flag-icons/css/flag-icons.min.css';
import '../styles.css';

function getFlagEmoji(country) {
  const code = countryCodes[country];
  if (!code) return { code: '' };
  return { code: code.toLowerCase() };
}

function CircuitManager({ isModal = false, onClose }) {
  const [user, loading, error] = useAuthState(auth);
  const [circuits, setCircuits] = useState([]);
  const [syncStatus, setSyncStatus] = useState('syncing');
  const [showCircuitEditor, setShowCircuitEditor] = useState(false);
  const [editingCircuit, setEditingCircuit] = useState(null);
  const [showCircuitDatabase, setShowCircuitDatabase] = useState(false);
  const [circuitToDelete, setCircuitToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) {
      setSyncStatus('Loading...');
      return;
    }
    if (error || !user) {
      console.error('Auth error or no user:', error);
      setSyncStatus('Error: Not logged in');
      return;
    }

    // Real-time listener for user circuits
    const circuitsQuery = query(
      collection(db, `users/${user.uid}/circuits`),
      orderBy('name')
    );
    const unsubscribe = onSnapshot(circuitsQuery, (snapshot) => {
      const userCircuits = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCircuits(userCircuits);
      setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
    }, (err) => {
      console.error('Circuits fetch error:', err.message);
      setSyncStatus(`Error: ${err.message}`);
    });

    // Update sync status on connectivity
    const updateSyncStatus = () => {
      setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
    };
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);

    return () => {
      unsubscribe();
      window.removeEventListener('online', updateSyncStatus);
      window.removeEventListener('offline', updateSyncStatus);
    };
  }, [user, loading, error]);



  const deleteCircuit = async (circuitId) => {
    setCircuitToDelete(circuitId);
  };

  const confirmDeleteCircuit = async () => {
    try {
      await deleteDoc(doc(db, `users/${user.uid}/circuits/${circuitToDelete}`));
      setSyncStatus('Synced');
    } catch (err) {
      console.error('CircuitManager.js: Error deleting circuit:', err);
      setSyncStatus(`Error: ${err.message}`);
      alert(`Failed to delete circuit: ${err.message}`);
    }
    setCircuitToDelete(null);
  };

  const editCircuit = (circuit) => {
    setEditingCircuit(circuit);
    setShowCircuitEditor(true);
  };

  return (
    <div>
      {!isModal && <Header syncStatus={syncStatus} />}
      <div className="page-title">
        <h1>Circuit Manager</h1>
        <button onClick={isModal ? onClose : () => navigate('/')} className="close-button">✕</button>
      </div>
      <div className="tire-manager-container">
        <div className="table-container">
          {circuits.length === 0 && <p>No circuits found. Add one to get started.</p>}
          <table className="full-size-table">
          <thead>
            <tr>
              <th></th>
              <th>Circuit Name</th>
              <th>Length</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {circuits.map(circuit => {
              const { code } = getFlagEmoji(circuit.country);
              return (
                <tr key={circuit.id}>
                  <td className="flag-column">
                    <span className={`fi fi-${code}`} title={circuit.country}></span>
                  </td>
                  <td onClick={() => editCircuit(circuit)} style={{ cursor: 'pointer' }}>{circuit.name}</td>
                  <td>{circuit.layouts ? circuit.layouts[0].length : circuit.length} km</td>
                  <td>
                    <button
                      onClick={() => editCircuit(circuit)}
                      className="btn btn-group-action edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => deleteCircuit(circuit.id)}
                      className="btn btn-group-action delete"
                    >
                      −
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
      <div className="session-buttons">
        <button
          onClick={() => {
            setEditingCircuit(null);
            setShowCircuitEditor(true);
          }}
          className="btn btn-save"
        >
          Add Custom Circuit
        </button>
        <button
          onClick={() => setShowCircuitDatabase(true)}
          className="btn btn-save"
        >
          Open Circuit Database
        </button>
      </div>
      {showCircuitEditor && (
        <CircuitEditor
          circuit={editingCircuit}
          onClose={() => setShowCircuitEditor(false)}
          onSave={() => setShowCircuitEditor(false)} // CircuitEditor saves directly
        />
      )}
      {showCircuitDatabase && (
        <CircuitDatabase
          onClose={() => setShowCircuitDatabase(false)}
          onSelect={(circuit) => {
            // Save selected circuit to user's collection
            setDoc(doc(db, `users/${user.uid}/circuits/${circuit.id}`), {
              ...circuit,
              timestamp: Date.now()
            }).then(async () => {
              await updateLastModified(user);
              setSyncStatus('Synced');
            }).catch(err => {
              console.error('CircuitManager.js: Error saving selected circuit:', err);
              setSyncStatus(`Error: ${err.message}`);
            });
            setShowCircuitDatabase(false);
          }}
        />
      )}
      {circuitToDelete && (
        <ConfirmationDialog
          message="Are you sure you want to delete this circuit?"
          onConfirm={confirmDeleteCircuit}
          onCancel={() => setCircuitToDelete(null)}
        />
      )}
    </div>
  );
}

export default CircuitManager;